import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { api } from '../../services/api';
import Modal from '../Modal';
import EntityPicker from '../ui/EntityPicker';
import { InlineError, apiErrorMessage } from './shared';
import type { Contract } from '../../types';
import DateInput from '../ui/DateInput';

export interface ContractSourceQuote {
  id: string;
  quote_number: number;
  revision_number?: number;
  title: string;
}

/**
 * Create a draft Contract from an ACCEPTED quote (decision 51: the contract is
 * its own entity; its binding terms are entered later by the user — nothing is
 * generated here).
 */
export default function CreateContractFromQuoteModal({
  quotes,
  onClose,
  onCreated,
}: {
  /** One or more accepted quotes; with several the user picks one. */
  quotes: ContractSourceQuote[];
  onClose: () => void;
  onCreated: (contract: Contract) => void;
}) {
  const { t } = useTranslation();
  const [quoteId, setQuoteId] = useState(quotes.length === 1 ? quotes[0].id : '');
  const selected = quotes.find((q) => q.id === quoteId);
  const [title, setTitle] = useState(quotes.length === 1 ? quotes[0].title : '');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // DQ-094: `isPending` only disables the button after the next render, so a
  // fast double click sent two requests (two contracts). A ref blocks it now.
  const inFlight = useRef(false);
  const mutation = useMutation({
    mutationFn: () =>
      api.createContractFromQuote(quoteId, {
        title: title.trim() || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      }),
    onSuccess: (res) => {
      if (res.data) onCreated(res.data);
    },
    onSettled: () => { inFlight.current = false; },
  });
  const submit = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    mutation.mutate();
  };

  const datesInvalid = !!startDate && !!endDate && endDate < startDate;
  const label = (q: ContractSourceQuote) =>
    `#${q.quote_number}${q.revision_number && q.revision_number > 1 ? `/${q.revision_number}` : ''} · ${q.title}`;

  return (
    <Modal open title={t('contracts.fromQuote.title')} onClose={onClose} sizeClass="max-w-md"
      icon={<ScrollText size={18} className="text-accent-text" />}>
      <div className="space-y-4">
        <p className="text-sm text-surface-600">{t('contracts.fromQuote.hint')}</p>
        {quotes.length > 1 ? (
          <div>
            <label htmlFor="contract-from-quote-1" className="label">{t('contracts.fromQuote.quote')} *</label>
            {/* Required: no "clear" (the create button also stays off without a quote). */}
            <EntityPicker
              id="contract-from-quote-1"
              value={quoteId}
              required
              clearable={false}
              placeholder={t('contracts.fromQuote.selectQuote')}
              onChange={(id) => {
                setQuoteId(id);
                const q = quotes.find((x) => x.id === id);
                if (q && !title.trim()) setTitle(q.title);
              }}
              options={quotes.map((q) => ({ value: q.id, label: label(q), keywords: String(q.quote_number ?? '') }))}
            />
          </div>
        ) : selected && (
          <p className="text-sm text-surface-800 font-medium">{label(selected)}</p>
        )}
        <div>
          <label htmlFor="contract-from-quote-2" className="label">{t('contracts.fields.title')}</label>
          <input id="contract-from-quote-2" className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="contract-from-quote-3" className="label">{t('contracts.fields.startDate')}</label>
            <DateInput id="contract-from-quote-3" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label htmlFor="contract-from-quote-4" className="label">{t('contracts.fields.endDate')}</label>
            <DateInput id="contract-from-quote-4" className="input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
        {datesInvalid && <p className="text-xs text-danger-700">{t('contracts.datesInvalid')}</p>}
        {mutation.isError && <InlineError>{apiErrorMessage(mutation.error, t('contracts.saveError'))}</InlineError>}
        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" disabled={!quoteId || datesInvalid || mutation.isPending}
            onClick={submit}>
            {mutation.isPending ? t('common.creating') : t('contracts.fromQuote.create')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
